import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import toast from 'react-hot-toast'
import { User } from '../types'
import { apiClient } from '../utils/api'

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
})

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  loginWithMicrosoft: () => void
  refreshUser: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuthContext must be used within AuthProvider')
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)

  const getToken = useCallback(async (): Promise<string | null> => {
    // Microsoft OAuth token takes priority — skip Cognito SDK entirely
    // (SDK's getSession() throws "string did not match expected pattern" on OAuth tokens)
    const msToken = localStorage.getItem('trc_ms_id_token')
    if (msToken) return msToken

    // Fallback: try Cognito SDK session (for playwright/test accounts only).
    // A corrupt/OAuth-format token in CognitoIdentityServiceProvider storage
    // makes the SDK throw a synchronous DOMException ("The string did not
    // match the expected pattern") from its internal atob(). We must clear
    // that storage and return null so the app cleanly redirects to /login
    // instead of surfacing a raw "Access Denied" throw on a deep link.
    try {
      const cognitoUser = userPool.getCurrentUser()
      if (!cognitoUser) return null

      return await new Promise((resolve) => {
        try {
          cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session?.isValid()) {
              resolve(null)
            } else {
              resolve(session.getIdToken().getJwtToken())
            }
          })
        } catch {
          resolve(null)
        }
      })
    } catch {
      // Clear any corrupt Cognito SDK keys so the next load starts clean.
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('CognitoIdentityServiceProvider'))
          .forEach((k) => localStorage.removeItem(k))
      } catch { /* ignore */ }
      return null
    }
  }, [])

  const refreshUserProfile = useCallback(async () => {
    try {
      const response = await apiClient.get('/users/me')
      if (response.data?.success && response.data?.data) {
        setUser(response.data.data)
        return true
      }
    } catch {
      // Profile doesn't exist yet — that's OK for first login
    }
    return false
  }, [])

  const initializeAuth = useCallback(async () => {
    try {
      setIsLoading(true)
      const token = await getToken()
      if (token) {
        const profileOk = await refreshUserProfile()
        if (profileOk) {
          setIsAuthenticated(true)
        } else {
          // Token exists but profile fetch failed — stale/expired session
          localStorage.removeItem('trc_ms_id_token')
          setIsAuthenticated(false)
          setUser(null)
        }
      }
    } catch {
      localStorage.removeItem('trc_ms_id_token')
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [getToken, refreshUserProfile])

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool })
      const authDetails = new AuthenticationDetails({ Username: email, Password: password })

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async () => {
          await refreshUserProfile()
          setIsAuthenticated(true)
          toast.success('Signed in')
          resolve(true)
        },
        onFailure: (err) => {
          const msg = err.code === 'NotAuthorizedException' ? 'Invalid email or password'
            : err.code === 'UserNotFoundException' ? 'User not found'
            : err.message || 'Sign in failed'
          toast.error(msg)
          resolve(false)
        },
        newPasswordRequired: () => {
          toast.error('Password change required — contact admin')
          resolve(false)
        },
      })
    })
  }, [refreshUserProfile])

  const loginWithMicrosoft = useCallback(() => {
    const domain = import.meta.env.VITE_COGNITO_DOMAIN
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
    const identityProvider = import.meta.env.VITE_COGNITO_IDENTITY_PROVIDER
    const redirect = `${window.location.origin}/callback`
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: redirect,
      identity_provider: identityProvider,
    })
    window.location.href = `https://${domain}/oauth2/authorize?${params.toString()}`
  }, [])

  const logout = useCallback(async () => {
    try { const u = userPool.getCurrentUser(); if (u) u.signOut() } catch { /* ignore SDK errors */ }
    localStorage.removeItem('trc_ms_id_token')
    localStorage.removeItem('trc_ms_access_token')
    localStorage.removeItem('trc_ms_refresh_token')
    localStorage.removeItem('trc_ms_provider')
    setIsAuthenticated(false)
    setUser(null)
    toast.success('Signed out')
  }, [])

  const refreshUser = useCallback(async () => {
    await refreshUserProfile()
  }, [refreshUserProfile])

  useEffect(() => { initializeAuth() }, [initializeAuth])

  return (
    <AuthContext.Provider value={{
      isAuthenticated, isLoading, user, login, logout,
      loginWithMicrosoft, refreshUser, getToken,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
